import { shareReplayCold } from '@principle-theorem/shared';
import { of } from 'rxjs';
import {
  type IManagementTool,
  type IResourceContext,
  type IResourceToolGroup,
  type IToolListItem,
  type IToolRunContext,
} from './models/management-tool';

export class ToolGroupResolver {
  static async resolve(
    contexts: IResourceContext[],
    tools: IManagementTool[]
  ): Promise<IResourceToolGroup[]> {
    const groups: IResourceToolGroup[] = [];

    const globalTools = tools.filter((tool) => !tool.resource);
    const resolvedGlobalTools = (
      await Promise.all(
        globalTools.map((tool) => ToolGroupResolver._resolveToolItem(tool))
      )
    ).filter((item) => !item.hidden);

    if (resolvedGlobalTools.length) {
      groups.push({
        resolvedLabel$: of('General'),
        tools: resolvedGlobalTools,
      });
    }

    const sidebarContexts = contexts.filter(
      (context) => !context.resource.hideInSidebar
    );
    const nonSidebarContexts = contexts.filter(
      (context) => context.resource.hideInSidebar
    );

    for (const context of sidebarContexts) {
      const matchingTools = tools.filter(
        (tool) => tool.resource === context.resource.resource
      );
      const foldedTools = this._findFoldedTools(
        context,
        nonSidebarContexts,
        tools
      );
      const allContextTools = [...matchingTools, ...foldedTools];
      const uniqueTools = this._deduplicateTools(allContextTools);

      const resolvedTools = (
        await Promise.all(
          uniqueTools.map((tool) =>
            ToolGroupResolver._resolveToolItem(tool, context)
          )
        )
      ).filter((item) => !item.hidden);

      if (resolvedTools.length) {
        groups.push({
          resource: context,
          resolvedLabel$: context.label$,
          tools: resolvedTools,
        });
      }
    }

    return groups;
  }

  /**
   * Find tools from non-sidebar contexts that share the same
   * routeDataKey as the given sidebar context, so they can be
   * folded into the sidebar group.
   */
  private static _findFoldedTools(
    sidebarContext: IResourceContext,
    nonSidebarContexts: IResourceContext[],
    tools: IManagementTool[]
  ): IManagementTool[] {
    const matchingNonSidebarContexts = nonSidebarContexts.filter(
      (context) => context.routeDataKey === sidebarContext.routeDataKey
    );

    return matchingNonSidebarContexts.flatMap((context) =>
      tools.filter((tool) => tool.resource === context.resource.resource)
    );
  }

  private static _deduplicateTools(
    tools: IManagementTool[]
  ): IManagementTool[] {
    const seen = new Set<string>();
    return tools.filter((tool) => {
      if (seen.has(tool.name)) {
        return false;
      }
      seen.add(tool.name);
      return true;
    });
  }

  private static async _resolveToolItem(
    tool: IManagementTool,
    matchingResource?: IResourceContext
  ): Promise<IToolListItem> {
    const context: IToolRunContext = {
      resource: matchingResource,
    };
    const disabled$ = tool.isDisabled
      ? tool.isDisabled(context).pipe(shareReplayCold())
      : of(false);
    const hidden = tool.isHidden ? await tool.isHidden(context) : false;
    return { tool, matchingResource, disabled$, hidden };
  }
}
